import { ChangeEvent, FunctionComponent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";

import { Modal, Icon } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import { TextArea } from "@/components/TextArea/TextArea";
import { ERROR_TYPE, renderErrorMessages } from "@/utils";

import {
	CreateSignalPriorityItem,
	CreateSignalPriorityItemSchema,
	SignalPriorityLevel,
	SignalPriorityItem,
} from "../schema/signal";

interface PriorityItemModalProps {
	isOpen: boolean;
	priorityLevel: SignalPriorityLevel;
	item?: SignalPriorityItem | null;
	onClose: () => void;
	onSave: (item: CreateSignalPriorityItem & { id?: number }) => void;
}

const getPriorityLabel = (level: SignalPriorityLevel): string => {
	switch (level) {
		case SignalPriorityLevel.PRIORITY_1:
			return "Priority 1";
		case SignalPriorityLevel.PRIORITY_2:
			return "Priority 2";
		case SignalPriorityLevel.PRIORITY_3:
			return "Priority 3";
		case SignalPriorityLevel.DISQUALIFIED:
			return "Disqualified";
		default:
			return "Unknown";
	}
};

export const PriorityItemModal: FunctionComponent<PriorityItemModalProps> = ({
	isOpen,
	priorityLevel,
	item,
	onClose,
	onSave,
}) => {
	const [examples, setExamples] = useState<string[]>(item?.examples || []);
	const [newExample, setNewExample] = useState("");

	const {
		handleSubmit,
		formState: { errors },
		setValue,
		watch,
		reset,
	} = useForm<CreateSignalPriorityItem>({
		defaultValues: {
			priorityLevel,
			title: item?.title || "",
			definition: item?.definition || "",
			examples: item?.examples || [],
			order: item?.order || 0,
		},
		resolver: zodResolver(CreateSignalPriorityItemSchema),
	});

	// Reset form and local state when item prop changes
	useEffect(() => {
		const newExamples = item?.examples || [];

		setExamples(newExamples);
		setNewExample("");
		reset({
			priorityLevel,
			title: item?.title || "",
			definition: item?.definition || "",
			examples: newExamples,
			order: item?.order || 0,
		});
	}, [item, priorityLevel, reset]);

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const handleAddExample = () => {
		if (newExample.trim()) {
			const updatedExamples = [...examples, newExample.trim()];

			setExamples(updatedExamples);
			setValue("examples", updatedExamples, { shouldValidate: true });
			setNewExample("");
		}
	};

	const handleRemoveExample = (index: number) => {
		const updatedExamples = examples.filter((_, i) => i !== index);

		setExamples(updatedExamples);
		setValue("examples", updatedExamples, { shouldValidate: true });
	};

	const onSubmit = (values: CreateSignalPriorityItem) => {
		onSave({
			...values,
			examples,
			id: item?.id,
		});
		onClose();
	};

	return (
		<Modal
			handleClose={onClose}
			handleSave={handleSubmit(onSubmit)}
			isOpened={isOpen}
			size="sm"
			submitButtonText={item ? "Update" : "Add"}
			title={`${item ? "Edit" : "Add"} ${getPriorityLabel(priorityLevel)} Item`}
		>
			<div className="space-y-4">
				<InputField
					error={errors.title?.message}
					handleChange={(e: ChangeEvent<HTMLInputElement>) =>
						setValue("title", e.target.value, { shouldValidate: true })
					}
					label="Title"
					name="title"
					placeholder="e.g., Buying Intent"
					value={watch("title")}
				/>

				<TextArea
					error={errors.definition?.message}
					handleChange={(e) => setValue("definition", e.target.value, { shouldValidate: true })}
					label="Definition"
					name="definition"
					placeholder="e.g., Signals indicating active purchase consideration"
					showError={!!errors.definition}
					value={watch("definition")}
				/>

				<div>
					<label className="block text-sm font-medium text-gray-700 mb-2">Examples</label>
					{examples.length > 0 && (
						<div className="space-y-2 mb-3">
							{examples.map((example, index) => (
								<div key={index} className="flex items-center gap-2">
									<span className="flex-1 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded">
										{example}
									</span>
									<button
										className="p-2 rounded hover:bg-gray-100 transition-colors"
										type="button"
										onClick={() => handleRemoveExample(index)}
									>
										<Icon className="h-4 w-4 text-status-error" icon={faTrash} />
									</button>
								</div>
							))}
						</div>
					)}
					<div className="flex items-center gap-2">
						<InputField
							className="flex-1 -mb-3"
							handleChange={(e: ChangeEvent<HTMLInputElement>) => setNewExample(e.target.value)}
							name="newExample"
							placeholder='"e.g., "Looking to invest in..."'
							value={newExample}
							onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddExample())}
						/>
						<button
							className="p-2 rounded hover:bg-gray-100 transition-colors text-brand-light"
							type="button"
							onClick={handleAddExample}
						>
							<Icon className="h-4 w-4" icon={faPlus} />
						</button>
					</div>
					{errors.examples && (
						<p className="text-xs text-status-error mt-1">{errors.examples.message}</p>
					)}
				</div>

				{formErrors.length > 0 && renderErrorMessages(formErrors)}
			</div>
		</Modal>
	);
};
